var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { withDone } from '@rad/utility/http';
import { Instant, LocalDate, ZoneId } from '../../utility/datetime';
import { computeProcessingFees, invoicePaymentMethod } from '../../utility/calculations';
import { requireToken, navigate } from '../../utility/general';
import { getBlob, getJson, getString, postRawJson } from '../../utility/request';
import { fetchAccountsReceivableAging, fetchInvoices, fetchPerformers, fetchReportInvoices } from '../../http/invoices';
import { fetchAppointments } from '../../http/appointments';
import Button from '../components/button';
import CInput from '../components/input';
import ReportAppointments from '../reports/appointments';
import ReportAccountsReceivableAging, { agingAge, agingBucket } from '../reports/accounts-receivable-aging';
import ReportClients from '../reports/clients';
import ReportDaily from '../reports/daily';
import ReportDailyActivity from '../reports/daily-activity';
import ReportGrossProfit from '../reports/gross-profit';
import ReportInvoices from '../reports/invoices';
import ReportSettlements from '../reports/settlements';
import ReportItems from '../reports/items';
import ReportPartsTechOrders from '../reports/partstech-orders';
import ReportTechnicianBilledTime from '../reports/technician-billed-time';
import { parseInstant } from '../../utility/parsers';
const grossProfitPresets = ['mtd', 'qtd', 'ytd', 'last-quarter', 'last-year', 'custom'];
const defaultGrossProfitPreset = 'mtd';
const isGrossProfitPreset = (value) => grossProfitPresets.includes(value);
const roundMoney = (value) => Math.round(value * 100) / 100;
const quarterStart = (date) => LocalDate.of(date.year(), Math.floor((date.monthValue() - 1) / 3) * 3 + 1, 1);
const grossProfitRange = (preset, today = LocalDate.now()) => {
    switch (preset) {
        case 'mtd':
            return { 'from': today.withDayOfMonth(1).toString(), 'to': today.toString() };
        case 'qtd':
            return { 'from': quarterStart(today).toString(), 'to': today.toString() };
        case 'ytd':
            return { 'from': LocalDate.of(today.year(), 1, 1).toString(), 'to': today.toString() };
        case 'last-quarter':
            {
                const currentQuarter = quarterStart(today);
                const end = currentQuarter.minusDays(1);
                return { 'from': quarterStart(end).toString(), 'to': end.toString() };
            }
        case 'last-year':
            return { 'from': LocalDate.of(today.year() - 1, 1, 1).toString(), 'to': LocalDate.of(today.year() - 1, 12, 31).toString() };
        case 'custom':
            return { 'from': today.withDayOfMonth(1).toString(), 'to': today.toString() };
        default:
            {
                const _ = preset;
                return _;
            }
    }
};
const defaultFrom = (report) => (report === 'appointments'
    ? LocalDate.now().toString()
    : ['daily', 'daily-activity', 'settlements', 'ar-aging'].includes(report)
        ? LocalDate.now().toString()
        : LocalDate.now().withDayOfMonth(1).toString());
const defaultTo = (report) => (report === 'appointments'
    ? LocalDate.now().plusDays(14).toString()
    : LocalDate.now().toString());
const ReportWrapper = (props) => {
    const token = requireToken('merchant');
    const reportRef = React.useRef(null);
    const downloadLinkRef = React.useRef(null);
    const [from, setFrom] = React.useState(defaultFrom(props.report));
    const [to, setTo] = React.useState(defaultTo(props.report));
    const [records, setRecords] = React.useState(undefined);
    const [download, setDownload] = React.useState(null);
    const [selectedUser, setSelectedUser] = React.useState('');
    const [selectedCategory, setSelectedCategory] = React.useState('');
    const [selectedItem, setSelectedItem] = React.useState('');
    const [selectedTechnician, setSelectedTechnician] = React.useState('');
    const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState('');
    const [grossProfitPreset, setGrossProfitPreset] = React.useState(defaultGrossProfitPreset);
    const [users, setUsers] = React.useState(null);
    const [categories, setCategories] = React.useState(null);
    const [availableItems, setAvailableItems] = React.useState(null);
    const [technicians, setTechnicians] = React.useState(null);
    const dataDaily = () => __awaiter(void 0, void 0, void 0, function* () { return [yield getString(`/merchants/${token.merchant.id}/reports/daily/${from}`)]; });
    const dataDailyActivity = () => __awaiter(void 0, void 0, void 0, function* () {
        const qs = selectedTechnician ? `?technician=${selectedTechnician}` : '';
        const raw = yield getJson(`/merchants/${token.merchant.id}/reports/daily-activity/${from}${qs}`);
        return [{
                estimates_created: raw.estimates_created.map((x) => (Object.assign(Object.assign({}, x), { occurred: parseInstant(x.occurred) }))),
                estimates_accepted: raw.estimates_accepted.map((x) => (Object.assign(Object.assign({}, x), { occurred: parseInstant(x.occurred) }))),
                repair_orders_created: raw.repair_orders_created.map((x) => (Object.assign(Object.assign({}, x), { occurred: parseInstant(x.occurred) }))),
                repair_orders_closed: raw.repair_orders_closed.map((x) => (Object.assign(Object.assign({}, x), { occurred: parseInstant(x.occurred) }))),
                invoices_paid: raw.invoices_paid.map((x) => (Object.assign(Object.assign({}, x), { paid: parseInstant(x.paid) }))),
                parts_ordered: raw.parts_ordered.map((x) => (Object.assign(Object.assign({}, x), { ordered: parseInstant(x.ordered) }))),
                appointments_scheduled: raw.appointments_scheduled.map((x) => (Object.assign(Object.assign({}, x), { appointment: parseInstant(x.appointment) }))),
                customers_created: raw.customers_created.map((x) => (Object.assign(Object.assign({}, x), { created: parseInstant(x.created) }))),
            }];
    });
    const dataInvoices = (report) => __awaiter(void 0, void 0, void 0, function* () {
        return (yield withDone(fetchReportInvoices(token.merchant.id, report, from, to))())
            .map((x) => (Object.assign(Object.assign({}, x), { date: !!x.payments.length ? x.payments[0].paid : !!x.closed ? x.closed.atZone(ZoneId.SYSTEM).toLocalDate() : x.created.atZone(ZoneId.SYSTEM).toLocalDate() })))
            .map((x) => {
            var _a, _b;
            const cardBased = ((_b = (_a = x.payments.find((y) => y.kind === 'card')) === null || _a === void 0 ? void 0 : _a.kind) !== null && _b !== void 0 ? _b : 'cash') === 'card' || x.disable_discount;
            return Object.assign(Object.assign({}, x), { nontaxable: cardBased ? x.totals_card.nontaxable : x.totals_noncard.nontaxable, taxable: cardBased ? x.totals_card.taxable : x.totals_noncard.taxable, subtotal: cardBased ? x.totals_card.subtotal : x.totals_noncard.subtotal, tax: cardBased ? x.totals_card.tax : x.totals_noncard.tax, total: cardBased ? x.totals_card.total : x.totals_noncard.total });
        });
    });
    const dataAccountsReceivableAging = () => __awaiter(void 0, void 0, void 0, function* () {
        const asOf = LocalDate.parse(from);
        return (yield withDone(fetchAccountsReceivableAging(token.merchant.id, from))())
            .map((x) => {
            var _a;
            const closed = ((_a = x.closed) === null || _a === void 0 ? void 0 : _a.atZone(ZoneId.SYSTEM).toLocalDate()) || asOf;
            const basis = x.on_account && x.due_date ? x.due_date : closed;
            return Object.assign(Object.assign({}, x), { age: agingAge(basis, asOf), bucket: agingBucket(basis, asOf) });
        });
    });
    const dataClients = () => __awaiter(void 0, void 0, void 0, function* () { return (yield getJson(`/merchants/${token.merchant.id}/clients`)).map((x) => (Object.assign({}, x))); });
    const dataAppointments = () => __awaiter(void 0, void 0, void 0, function* () { return withDone(fetchAppointments(token.merchant.id, 'year', from))(); });
    const dataSettlements = () => __awaiter(void 0, void 0, void 0, function* () { return [yield getString(`/merchants/${token.merchant.id}/reports/settlements/${from}`)]; });
    const dataTechnicianBilledTime = () => __awaiter(void 0, void 0, void 0, function* () {
        const qs = selectedTechnician ? `?technician=${selectedTechnician}` : '';
        const raw = yield getJson(`/merchants/${token.merchant.id}/reports/technician-billed-time/${from}/${to}${qs}`);
        return raw.map((x) => (Object.assign(Object.assign({}, x), { hours: typeof x.hours === 'string' ? parseFloat(x.hours) : x.hours, billed: typeof x.billed === 'string' ? parseFloat(x.billed) : x.billed, cost: typeof x.cost === 'string' ? parseFloat(x.cost) : x.cost })));
    });
    const dataGrossProfit = () => __awaiter(void 0, void 0, void 0, function* () {
        // Server returns invoices with ANY payment in [from,to). The Paid Invoice Profitability
        // view recognizes revenue on the date the invoice became fully paid, so we additionally
        // require the LATEST payment to land inside the selected range.
        const startDate = LocalDate.parse(from);
        const finishDate = LocalDate.parse(to);
        const categoryKeys = ['general', 'labor', 'parts', 'misc', 'ntnf', 'fee'];
        const records = (yield withDone(fetchReportInvoices(token.merchant.id, 'paid', from, to))())
            .filter((invoice) => invoice.classification_paid === 'fullpaid')
            .map((invoice) => {
            const paymentDate = invoice.payments.reduce((latest, payment) => (!latest || payment.paid.isAfter(latest)
                ? payment.paid
                : latest), null);
            if (!paymentDate || paymentDate.isBefore(startDate) || paymentDate.isAfter(finishDate)) {
                return null;
            }
            // Period-scope payments: total_collected and processing_fees should only count payments
            // whose paid date falls in the selected range. Older partials, refunds, or test data
            // attached to the same invoice would otherwise inflate both numbers.
            const paymentsInRange = invoice.payments.filter((p) => !p.paid.isBefore(startDate) && !p.paid.isAfter(finishDate));
            const basis = invoicePaymentMethod(invoice);
            const totalsKey = basis === 'card' ? 'totals_card' : 'totals_noncard';
            const totals = invoice[totalsKey];
            const paymentMethods = Array.from(new Set(paymentsInRange.map((payment) => payment.kind)));
            const categories = {
                'general': { 'gross_sales': 0, 'cost': 0, 'gross_profit': 0 },
                'labor': { 'gross_sales': 0, 'cost': 0, 'gross_profit': 0 },
                'parts': { 'gross_sales': 0, 'cost': 0, 'gross_profit': 0 },
                'misc': { 'gross_sales': 0, 'cost': 0, 'gross_profit': 0 },
                'ntnf': { 'gross_sales': 0, 'cost': 0, 'gross_profit': 0 },
                'fee': { 'gross_sales': 0, 'cost': 0, 'gross_profit': 0 },
            };
            invoice.items.forEach((item) => {
                const bucket = categories[item.kind];
                bucket.gross_sales += item[totalsKey].subtotal;
                bucket.cost += item.cost * item.quantity;
            });
            categoryKeys.forEach((key) => {
                const bucket = categories[key];
                bucket.gross_sales = roundMoney(bucket.gross_sales);
                bucket.cost = roundMoney(bucket.cost);
                bucket.gross_profit = roundMoney(bucket.gross_sales - bucket.cost);
            });
            // Whole-invoice discount is reported separately rather than allocated across categories.
            // Allocation policy is an unresolved product decision (see Dev Map/Calculations.md).
            const grossSales = roundMoney(categoryKeys.reduce((sum, key) => sum + categories[key].gross_sales, 0));
            const discount = roundMoney(totals.discount);
            const netSales = roundMoney(totals.subtotal);
            const cost = roundMoney(categoryKeys.reduce((sum, key) => sum + categories[key].cost, 0));
            const grossProfit = roundMoney(netSales - cost);
            const processingFees = roundMoney(computeProcessingFees({ 'payments': paymentsInRange, 'merchant': invoice.merchant }));
            return {
                'invoice_id': invoice.id,
                'merchant_id': invoice.merchant.id,
                'invoice_number': invoice.number,
                'client_firstname': invoice.client.firstname,
                'client_lastname': invoice.client.lastname,
                'payment_date': paymentDate,
                'payment_methods': paymentMethods,
                'basis': basis,
                'gross_sales': grossSales,
                'discount': discount,
                'net_sales': netSales,
                'tax': roundMoney(totals.tax),
                'total_collected': roundMoney(paymentsInRange.reduce((sum, payment) => sum + payment.amount, 0)),
                'cost': cost,
                'gross_profit': grossProfit,
                'processing_fees': processingFees,
                'gross_margin': netSales > 0 ? grossProfit / netSales : null,
                categories,
            };
        })
            .filter((invoice) => invoice !== null);
        return records.sort((a, b) => a.payment_date.compareTo(b.payment_date) || a.invoice_number - b.invoice_number);
    });
    const dataItems = () => __awaiter(void 0, void 0, void 0, function* () {
        const startDate = LocalDate.parse(from);
        const finishDate = LocalDate.parse(to);
        const invoices = (yield withDone(fetchReportInvoices(token.merchant.id, 'paid', from, to))())
            .filter((x) => x && x.items && x.items.length > 0 && x.classification_paid === 'fullpaid');
        const items = [];
        invoices.forEach((invoice) => {
            const paymentDate = invoice.payments.reduce((latest, p) => !latest || p.paid.isAfter(latest) ? p.paid : latest, null);
            if (!paymentDate || paymentDate.isBefore(startDate) || paymentDate.isAfter(finishDate))
                return;
            const basis = invoicePaymentMethod(invoice);
            const method = Array.from(new Set(invoice.payments.map((p) => p.kind))).join(',');
            invoice.items.forEach((item) => {
                var _a, _b;
                if (selectedUser && invoice.created_by_name !== selectedUser)
                    return;
                if (selectedCategory && item.kind !== selectedCategory)
                    return;
                if (selectedItem && item.description !== selectedItem)
                    return;
                const cashPrice = item.totals_noncard.subtotal;
                const cardPrice = item.totals_card.subtotal;
                items.push({
                    id: `${invoice.id}-${item.id}`,
                    kind: item.kind,
                    description: item.description,
                    part_number: item.source === 'partstech' ? (_b = (_a = item.meta) === null || _a === void 0 ? void 0 : _a.part_number) !== null && _b !== void 0 ? _b : null : null,
                    source: item.source,
                    quantity: item.quantity,
                    unit_cost: item.cost,
                    total_cost: item.cost * item.quantity,
                    cash_unit_price: item.totals_noncard.rate,
                    card_unit_price: item.totals_card.rate,
                    cash_price: cashPrice,
                    card_price: cardPrice,
                    method,
                    amount_paid: basis === 'card' ? cardPrice : cashPrice,
                    taxable: item.taxable,
                    date: paymentDate,
                    merchant_id: invoice.merchant.id,
                    invoice_id: invoice.id,
                    invoice_number: invoice.number,
                    invoice,
                    client_id: invoice.client.id,
                    client_firstname: invoice.client.firstname,
                    client_lastname: invoice.client.lastname,
                    created_by: invoice.created_by,
                    created_by_name: invoice.created_by_name,
                });
            });
        });
        return items.sort((a, b) => a.date.compareTo(b.date) || a.invoice_number - b.invoice_number);
    });
    const loadFilterOptions = () => __awaiter(void 0, void 0, void 0, function* () {
        if (props.report !== 'items') {
            return;
        }
        const invoices = yield withDone(fetchInvoices(token.merchant.id))();
        const invoicesWithItems = invoices.filter((x) => x
            && x.items
            && x.items.length > 0
            && x.classification === 'invoice-paid');
        const uniqueUsers = new Set();
        const uniqueCategories = new Set();
        const uniqueItems = new Set();
        invoicesWithItems.forEach((invoice) => {
            uniqueUsers.add(invoice.created_by_name);
            invoice.items.forEach((item) => {
                uniqueCategories.add(item.kind);
                uniqueItems.add(`${item.kind}|${item.description}`);
            });
        });
        setUsers(Array.from(uniqueUsers).map((name) => ({ id: name, name })));
        setCategories(Array.from(uniqueCategories).sort());
        setAvailableItems(Array.from(uniqueItems).sort());
    });
    const loadTechnicians = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!['daily-activity', 'technician-billed-time'].includes(props.report)) {
            return;
        }
        const getPerformers = yield withDone(fetchPerformers(token.merchant.id))();
        setTechnicians(getPerformers(null).map((x) => ({ id: x.id, name: x.name })));
    });
    const doCsvDownload = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const content = (_a = reportRef.current) === null || _a === void 0 ? void 0 : _a.getContent();
        if (!content) {
            return;
        }
        const csv = `${content.headers.join(',')}\n${content.records.map((xs) => xs.map((x) => typeof x === 'string'
            ? x.indexOf(',') > -1 || x.indexOf('"') > -1 || x.indexOf('\n') > -1
                ? `"${x.replace(/"/g, '""')}"`
                : x
            : x === null || x === undefined
                ? ''
                : x).join(',')).join('\n')}`;
        const url = window.URL.createObjectURL(new Blob([csv]));
        setDownload({
            link: url,
            filename: `${reports[reportName].name}-${from.toString()}-to-${to.toString()}.${Instant.now()}.csv`,
        });
    });
    const doPdfDownload = () => __awaiter(void 0, void 0, void 0, function* () {
        const blob = yield getBlob(`/merchants/${token.merchant.id}/reports/daily/${from}/download`);
        const url = window.URL.createObjectURL(blob);
        setDownload({
            link: url,
            filename: `${reports[reportName].name}-${from.toString()}.pdf`,
        });
    });
    const doPrint = () => __awaiter(void 0, void 0, void 0, function* () {
        window.print();
    });
    const triggerSettlement = (invoiceId, action) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield postRawJson(`/merchants/${token.merchant.id}/invoices/${invoiceId}/settlement/${action}`, '');
            const responseData = JSON.parse(response);
            if (responseData.GeneralResponse.ResultCode === '0' && responseData.GeneralResponse.StatusCode === '0000') {
                alert(`Settlement ${action} successful.\n${responseData.GeneralResponse.DetailedMessage || responseData.GeneralResponse.Message || 'Transaction completed'}`);
            }
            else {
                alert(`Settlement ${action} failed.\n${responseData.GeneralResponse.DetailedMessage || responseData.GeneralResponse.Message || 'Transaction failed'}`);
            }
            yield refreshRecords();
        }
        catch (error) {
            console.error('Settlement error details:', error);
            alert(`Settlement failed: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
        }
    });
    const reports = {
        daily: {
            name: 'daily',
            title: 'Daily Transactions',
            dataset: dataDaily,
            filter: (_) => true,
            download: doPdfDownload,
            component: ReportDaily,
        },
        'daily-activity': {
            name: 'daily-activity',
            title: 'Daily Activity',
            dataset: dataDailyActivity,
            filter: (_) => true,
            download: doPrint,
            component: ReportDailyActivity,
        },
        paid: {
            name: 'paid-invoices',
            title: 'Paid Invoices',
            dataset: () => dataInvoices('paid'),
            filter: (x) => (!selectedPaymentMethod
                || x.payments.some((y) => y.kind === selectedPaymentMethod)),
            download: doCsvDownload,
            component: ReportInvoices,
        },
        'gross-profit': {
            name: 'gross-profit',
            title: 'Gross Profit',
            dataset: dataGrossProfit,
            filter: (_) => true,
            download: doCsvDownload,
            component: ReportGrossProfit,
        },
        unpaid: {
            name: 'unpaid-invoices',
            title: 'Unpaid Invoices',
            dataset: () => dataInvoices('unpaid'),
            filter: (_) => true,
            download: doCsvDownload,
            component: ReportInvoices,
        },
        appointments: {
            name: 'appointments',
            title: 'Appointments',
            dataset: dataAppointments,
            filter: (x) => (!x.appointment.atZone(ZoneId.SYSTEM).toLocalDate().isBefore(LocalDate.parse(from))
                && !x.appointment.atZone(ZoneId.SYSTEM).toLocalDate().isAfter(LocalDate.parse(to))),
            download: doCsvDownload,
            component: ReportAppointments,
        },
        settlements: {
            name: 'settlements',
            title: 'Settlements',
            dataset: dataSettlements,
            filter: (_) => true,
            download: doCsvDownload,
            component: ReportSettlements,
            settlement: triggerSettlement,
        },
        clients: {
            name: 'customers',
            title: 'Customers',
            dataset: dataClients,
            filter: (_) => true,
            download: doCsvDownload,
            component: ReportClients,
        },
        items: {
            name: 'report-by-item',
            title: 'Report by Item',
            dataset: dataItems,
            filter: (_) => true,
            download: doCsvDownload,
            component: ReportItems,
        },
        'ar-aging': {
            name: 'accounts-receivable-aging',
            title: 'Accounts Receivable / Aging',
            dataset: dataAccountsReceivableAging,
            filter: (_) => true,
            download: doCsvDownload,
            component: ReportAccountsReceivableAging,
        },
        'technician-billed-time': {
            name: 'technician-billed-time',
            title: 'Technician Billed Time',
            dataset: dataTechnicianBilledTime,
            filter: (_) => true,
            download: doCsvDownload,
            component: ReportTechnicianBilledTime,
        },
    };
    const [reportName, setReportName] = React.useState((props.report in reports ? props.report : 'paid'));
    const refreshRecords = () => __awaiter(void 0, void 0, void 0, function* () {
        const report = reports[reportName];
        const data = yield report.dataset();
        setRecords(data.filter((item) => report.filter(item)));
    });
    const runReport = () => __awaiter(void 0, void 0, void 0, function* () {
        setRecords(null);
        yield refreshRecords();
    });
    React.useEffect(() => {
        setReportName((props.report in reports ? props.report : 'paid'));
        setFrom(defaultFrom(props.report));
        setTo(defaultTo(props.report));
        setRecords(props.report === 'items' ? undefined : null);
        setSelectedUser('');
        setSelectedCategory('');
        setSelectedItem('');
        setSelectedTechnician('');
        setSelectedPaymentMethod('');
        setGrossProfitPreset(defaultGrossProfitPreset);
        setUsers(null);
        setCategories(null);
        setAvailableItems(null);
        setTechnicians(null);
        loadFilterOptions();
        loadTechnicians();
    }, [props.report]);
    React.useEffect(() => {
        if (reportName === 'items') {
            return;
        }
        refreshRecords();
    }, [reportName, from, to, selectedTechnician, selectedPaymentMethod]);
    React.useEffect(() => {
        if (reportName !== 'gross-profit' || grossProfitPreset === 'custom') {
            return;
        }
        const range = grossProfitRange(grossProfitPreset);
        setFrom(range.from);
        setTo(range.to);
    }, [reportName, grossProfitPreset]);
    React.useEffect(() => {
        if (!download || !downloadLinkRef.current) {
            return;
        }
        downloadLinkRef.current.click();
        window.URL.revokeObjectURL(download.link);
        setDownload(null);
    }, [download]);
    const report = reports[reportName];
    const recordsReady = Array.isArray(records) && records.length > 0;
    const renderReport = () => {
        if (!recordsReady) {
            return null;
        }
        switch (reportName) {
            case 'appointments':
                return _jsx(ReportAppointments, { ref: reportRef, records: records, report: reportName });
            case 'clients':
                return _jsx(ReportClients, { ref: reportRef, records: records, report: reportName });
            case 'daily':
                return _jsx(ReportDaily, { ref: reportRef, records: records, report: reportName });
            case 'daily-activity':
                return _jsx(ReportDailyActivity, { ref: reportRef, records: records, report: reportName });
            case 'ar-aging':
                return _jsx(ReportAccountsReceivableAging, { ref: reportRef, records: records, report: reportName, asOf: LocalDate.parse(from) });
            case 'paid':
            case 'unpaid':
                return _jsx(ReportInvoices, { ref: reportRef, records: records, report: reportName });
            case 'gross-profit':
                return _jsx(ReportGrossProfit, { ref: reportRef, records: records, report: reportName });
            case 'settlements':
                return _jsx(ReportSettlements, { ref: reportRef, records: records, report: reportName });
            case 'items':
                return _jsx(ReportItems, { ref: reportRef, records: records, report: reportName, category: selectedCategory });
            case 'technician-billed-time':
                return _jsx(ReportTechnicianBilledTime, { ref: reportRef, records: records, report: reportName });
            default:
                return null;
        }
    };
    return _jsxs("div", { className: "d-flex flex-column w-100", children: [_jsx("div", { className: "mb-2", children: _jsxs("a", { href: "#/reports", className: "text-decoration-none d-inline-flex align-items-center", style: { 'cursor': 'pointer' }, onClick: (e) => { e.preventDefault(); navigate('#/reports'); }, children: [_jsx("i", { className: "material-symbols-outlined me-1", style: { 'fontSize': '20px' }, children: "arrow_back" }), _jsx("span", { children: "All Reports" })] }) }), _jsxs("div", { className: "d-flex toolbar", children: [['daily', 'daily-activity', 'settlements', 'ar-aging'].includes(reportName)
                        ? _jsx(CInput, { type: "date", value: from, onChange: setFrom, inline: true, small: true, children: reportName === 'ar-aging' ? 'As Of' : 'Date' })
                        : reportName !== 'clients'
                            ? _jsxs(_Fragment, { children: [reportName === 'gross-profit' && _jsxs("select", { className: "form-select form-select-sm w-auto", value: grossProfitPreset, onChange: (e) => { if (isGrossProfitPreset(e.target.value))
                                            setGrossProfitPreset(e.target.value); }, children: [_jsx("option", { value: "mtd", children: "MTD" }), _jsx("option", { value: "qtd", children: "QTD" }), _jsx("option", { value: "ytd", children: "YTD" }), _jsx("option", { value: "last-quarter", children: "Last Quarter" }), _jsx("option", { value: "last-year", children: "Last Year" }), _jsx("option", { value: "custom", children: "Custom" })] }), _jsx(CInput, { type: "date", value: from, onChange: (value) => { if (reportName === 'gross-profit')
                                            setGrossProfitPreset('custom'); setFrom(value); }, inline: true, small: true, className: reportName === 'gross-profit' ? 'ms-1' : '', children: "From" }), _jsx(CInput, { type: "date", value: to, onChange: (value) => { if (reportName === 'gross-profit')
                                            setGrossProfitPreset('custom'); setTo(value); }, inline: true, small: true, className: "ms-1", children: "To" })] })
                            : _jsx("div", { className: "flex-grow-1" }), (reportName === 'daily-activity' || reportName === 'technician-billed-time') && _jsxs("select", { className: "form-select form-select-sm ms-1 w-auto", value: selectedTechnician, onChange: (e) => setSelectedTechnician(e.target.value), children: [_jsx("option", { value: "", children: "All Technicians" }), (technicians || []).map((x) => _jsx("option", { value: x.id, children: x.name }, x.id))] }), reportName === 'paid' && _jsxs("select", { className: "form-select form-select-sm ms-1 w-auto", value: selectedPaymentMethod, onChange: (e) => setSelectedPaymentMethod(e.target.value), children: [_jsx("option", { value: "", children: "All Payment Methods" }), _jsx("option", { value: "cash", children: "Cash" }), _jsx("option", { value: "card", children: "Card" }), _jsx("option", { value: "check", children: "Check" }), _jsx("option", { value: "other", children: "Other" }), _jsx("option", { value: "write-off", children: "Write-Off" })] }), _jsx("a", { className: `ms-1 ${!recordsReady && 'disabled'}`, href: "#", onClick: (e) => {
                            e.preventDefault();
                            if (recordsReady) {
                                report.download();
                            }
                        }, children: _jsx("span", { className: "material-symbols-outlined", children: reportName === 'daily-activity' ? 'print' : 'download' }) }), !!download && _jsx("a", { href: download.link, download: download.filename, ref: downloadLinkRef, target: "_blank", rel: "noreferrer" })] }), _jsxs("div", { children: [reportName !== 'daily' && _jsx("h1", { className: "text-center", children: report.title }), reportName === 'items' && _jsxs("div", { className: "d-flex gap-3 mb-3", children: [_jsx("div", { className: "flex-fill", children: !!users
                                    ? _jsxs("select", { className: "form-select", value: selectedUser, onChange: (e) => setSelectedUser(e.target.value), children: [_jsx("option", { value: "", children: "Select User" }), users.map((user) => _jsx("option", { value: user.id, children: user.name }, user.id))] })
                                    : _jsx(_Fragment, { children: "Loading..." }) }), _jsx("div", { className: "flex-fill", children: !!categories
                                    ? _jsxs("select", { className: "form-select", value: selectedCategory, onChange: (e) => setSelectedCategory(e.target.value), children: [_jsx("option", { value: "", children: "Select Category" }), categories.map((category) => _jsx("option", { value: category, children: category }, category))] })
                                    : _jsx(_Fragment, { children: "Loading..." }) }), _jsx("div", { className: "flex-fill", children: !!availableItems
                                    ? _jsxs("select", { className: "form-select", value: selectedItem, onChange: (e) => setSelectedItem(e.target.value), children: [_jsx("option", { value: "", children: "Select Item" }), availableItems
                                                .map((x) => x.split('|', 2))
                                                .filter((x) => !selectedCategory || x[0] === selectedCategory)
                                                .map((x) => x[1])
                                                .map((item) => _jsx("option", { value: item, children: item }, item))] })
                                    : _jsx(_Fragment, { children: "Loading..." }) }), _jsx(Button, { color: "primary", onClick: runReport, disabled: records === null, children: "Run Report" })] }), records === undefined && _jsx("div", { className: "alert alert-warning", role: "alert", children: "Select filters and Run Report" }), records === null && _jsx("div", { className: "alert alert-primary", role: "alert", children: "Loading..." }), Array.isArray(records) && !records.length && _jsx("div", { className: "alert alert-warning", role: "alert", children: "No Results" }), renderReport()] })] });
};
export default (props) => {
    switch (props.report) {
        case 'partstech-orders': return _jsx(ReportPartsTechOrders, {});
        default: return _jsx(ReportWrapper, { report: props.report });
    }
};
